import { useState } from "react";
import {
  useGetGalleryPhotosQuery,
  useUploadGalleryPhotoMutation,
  useUpdateGalleryPhotoMutation,
  useDeleteGalleryPhotoMutation,
} from "../../features/api/galleryApiSlice";
import FuelLoading from "../../pages/FuelLoading";
import "./adminGallery.scss";
import { useToast } from "../toast/ToastContext";
import { FaTrash, FaPen, FaMapMarkerAlt } from "react-icons/fa";
import ConfirmModal from "../modal/ConfirmModal";

const REGION_OPTIONS = [
  "Midwest",
  "Northeast",
  "South",
  "West",
  "Pacific Northwest",
  "General Overhead",
];

export default function AdminGallery() {
  const { showToast } = useToast();
  const { data: photos = [], isLoading } = useGetGalleryPhotosQuery();
  const [uploadGalleryPhoto, { isLoading: isUploading }] =
    useUploadGalleryPhotoMutation();
  const [updateGalleryPhoto, { isLoading: isUpdating }] =
    useUpdateGalleryPhotoMutation();
  const [deleteGalleryPhoto, { isLoading: isDeleting }] =
    useDeleteGalleryPhotoMutation();

  // Form input field elements tracker hooks matching our new schema
  const [title, setTitle] = useState("");
  const [townName, setTownName] = useState("");
  const [stateName, setStateName] = useState("");
  const [regionGroup, setRegionGroup] = useState("Midwest");
  const [caption, setCaption] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");

  // Tracks if we are editing an item or creating a fresh one
  const [editingId, setEditingId] = useState(null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // 🟢 NEW State: Custom confirmation modal tracking metrics parameters
  const [modalConfig, setModalConfig] = useState({
    isOpen: false,
    targetId: null,
    targetTitle: "",
  });

  const isMutating = isUploading || isUpdating || isDeleting;

  const handleFileChange = (e) => {
    if (e.target.files?.[0]) {
      const file = e.target.files[0];
      const sizeInMB = file.size / (1024 * 1024);

      if (sizeInMB > 15) {
        showToast(
          "This file exceeds 15MB. Please choose a slightly smaller variation asset.",
          "error"
        );
        setSelectedFile(null);
        e.target.value = "";
        return;
      }

      setSelectedFile(file);
      setPreviewUrl(URL.createObjectURL(file));
    }
  };

  const handleFormReset = () => {
    setTitle("");
    setTownName("");
    setStateName("");
    setRegionGroup("Midwest");
    setCaption("");
    setSelectedFile(null);
    setPreviewUrl("");
    setEditingId(null);
  };

  const handleEditSelect = (photo) => {
    setEditingId(photo._id);
    setTitle(photo.title || "");
    setTownName(photo.townName || "");
    setStateName(photo.stateName || "");
    setRegionGroup(photo.regionGroup || "Midwest");
    setCaption(photo.caption || "");
    setPreviewUrl(photo.imageUrl || "");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (
      !title.trim() ||
      !townName.trim() ||
      !stateName.trim() ||
      !regionGroup.trim()
    ) {
      setError(
        "Please complete all required location and region parameter fields."
      );
      return;
    }

    const formData = new FormData();
    formData.append("title", title.trim());
    formData.append("townName", townName.trim());
    formData.append("stateName", stateName.trim());
    formData.append("regionGroup", regionGroup);
    formData.append("caption", caption.trim());

    if (selectedFile) {
      formData.append("imageFile", selectedFile);
    }

    try {
      if (editingId) {
        await updateGalleryPhoto({ id: editingId, formData }).unwrap();
        setSuccess("🎉 Photo asset configurations updated successfully!");
      } else {
        if (!selectedFile) {
          setError("Please select an image file to upload.");
          return;
        }
        await uploadGalleryPhoto(formData).unwrap();
        setSuccess(
          "🎉 New photo successfully compressed and pushed to S3 bucket arrays!"
        );
      }
      handleFormReset();
      setTimeout(() => setSuccess(""), 4000);
    } catch (err) {
      setError(
        err?.data?.message ||
          "Failed to commit media asset operations down the road."
      );
    }
  };

  // 🟢 FIXED: Open custom confirmation modal drawer and track targeted row parameters
  const handleDeleteTrigger = (id, name) => {
    setModalConfig({
      isOpen: true,
      targetId: id,
      targetTitle: name,
    });
  };

  // 🟢 FIXED: Execute mutation purge once admin approves on the custom interface block
  const executePurgeAsset = async () => {
    const { targetId, targetTitle } = modalConfig;
    setModalConfig({ isOpen: false, targetId: null, targetTitle: "" });

    try {
      await deleteGalleryPhoto(targetId).unwrap();
      showToast(
        `"${targetTitle}" permanently removed from gallery collections.`,
        "success"
      );
      if (editingId === targetId) handleFormReset();
    } catch (err) {
      showToast("Failed to complete asset deletion sequence.", "error");
    }
  };

  if (isLoading) {
    return (
      <div className="admin-gallery-loading">
        <FuelLoading text="Opening photo vaults..." />
      </div>
    );
  }

  return (
    <div className="admin-gallery-dashboard fuel-fade-in">
      {/* 🟢 REUSABLE CONFIRMATION MODAL INTERCEPTOR INTERFACE VIEW */}
      <ConfirmModal
        isOpen={modalConfig.isOpen}
        title="Purge Cloud Media Asset"
        message={`Are you completely sure you want to delete "${modalConfig.targetTitle}"? This will permanently wipe the file from your AWS S3 bucket storage completely.`}
        onConfirm={executePurgeAsset}
        onCancel={() =>
          setModalConfig({ isOpen: false, targetId: null, targetTitle: "" })
        }
      />

      {isMutating && (
        <FuelLoading text="Synchronizing asset files indexes..." fullScreen />
      )}

      <div className="admin-gallery-grid">
        {/* COMPACT INTERACTIVE COMPOSITION FORM DRAWER */}
        <section className="gallery-form-panel">
          <h2>
            {editingId ? "✏️ Edit Photo Details" : "📷 Upload New Photo Asset"}
          </h2>
          <p>
            Stream images up to your cloud S3 bucket container, apply automatic
            optimization filters, and folder them state-by-state.
          </p>

          <form onSubmit={handleSubmit} className="settings-form-stack">
            {success && (
              <div className="gallery-alert gallery-alert--success">
                {success}
              </div>
            )}
            {error && (
              <div className="gallery-alert gallery-alert--error">{error}</div>
            )}

            <label className="gallery-field-row">
              Photo Description Title *
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g. Badlands Sunset"
                required
              />
            </label>

            <div className="form-double-fields-row">
              <label className="gallery-field-row">
                Town / City Name *
                <input
                  type="text"
                  value={townName}
                  onChange={(e) => setTownName(e.target.value)}
                  placeholder="e.g. Galena, Olin"
                  required
                />
              </label>

              <label className="gallery-field-row">
                US State Name *
                <input
                  type="text"
                  value={stateName}
                  onChange={(e) => setStateName(e.target.value)}
                  placeholder="e.g. Illinois"
                  required
                />
              </label>
            </div>

            <label className="gallery-field-row">
              Macro Regional Folder *
              <select
                value={regionGroup}
                onChange={(e) => setRegionGroup(e.target.value)}
              >
                {REGION_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            </label>

            <label className="gallery-field-row">
              Caption text (Optional background info)
              <textarea
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                placeholder="Type background road trip details..."
              />
            </label>

            <div className="gallery-file-uploader-box">
              {previewUrl ? (
                <div className="uploader-preview-wrapper">
                  <img src={previewUrl} alt="" />
                  <button
                    type="button"
                    className="clear-uploader-btn"
                    onClick={() => {
                      setSelectedFile(null);
                      setPreviewUrl("");
                    }}
                  >
                    Change File ✕
                  </button>
                </div>
              ) : (
                <div className="empty-uploader-slate">
                  <span className="icon">🖼️</span>
                  <input
                    type="file"
                    accept="image/*"
                    id="gallery-file"
                    onChange={handleFileChange}
                  />
                  <label htmlFor="gallery-file" className="file-trigger-btn">
                    Select Photo file
                  </label>
                  <span>
                    Supports images up to 15MB (Auto web-compressed under 1.5MB)
                  </span>
                </div>
              )}
            </div>

            <div className="form-action-buttons">
              <button
                type="submit"
                className="fuel-primary-btn"
                disabled={isMutating}
              >
                {editingId ? "Save Image Changes" : "🚀 Upload Image to S3"}
              </button>
              {editingId && (
                <button
                  type="button"
                  className="cancel-btn"
                  onClick={handleFormReset}
                >
                  Cancel
                </button>
              )}
            </div>
          </form>
        </section>

        {/* DATA MANAGEMENT ARCHIVE FEED ROWS */}
        <section className="gallery-feed-panel">
          <h2>Active Gallery Index ({photos.length})</h2>

          <div className="admin-gallery-scrolling-view-matrix">
            {photos.length === 0 ? (
              <p className="empty-feed-placeholder-text">
                No travel milestones logged down your gallery database
                registries yet.
              </p>
            ) : (
              <div className="admin-assets-grid-layout">
                {photos.map((photo) => (
                  <article
                    key={photo._id}
                    className={`admin-asset-media-card ${
                      editingId === photo._id ? "editing" : ""
                    }`}
                  >
                    <div className="thumb-box-frame">
                      <img
                        src={photo.imageUrl}
                        alt={photo.title}
                        loading="lazy"
                      />
                    </div>
                    <div className="asset-details-footer">
                      <h4>{photo.title}</h4>
                      <div className="location-row-meta">
                        <span>
                          <FaMapMarkerAlt /> {photo.townName}, {photo.stateName}
                        </span>
                        <span className="badge-region-group">
                          {photo.regionGroup}
                        </span>
                      </div>
                      {photo.caption && (
                        <p className="admin-caption-notes-text">
                          “{photo.caption}”
                        </p>
                      )}
                    </div>
                    <div className="card-controls-overlay-row">
                      <button
                        type="button"
                        className="btn-action edit"
                        onClick={() => handleEditSelect(photo)}
                        title="Edit Meta Tags"
                      >
                        <FaPen />
                      </button>
                      <button
                        type="button"
                        className="btn-action delete"
                        onClick={() =>
                          handleDeleteTrigger(photo._id, photo.title)
                        }
                        title="Wipe from Cloud and DB"
                      >
                        <FaTrash />
                      </button>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}
